import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Cta } from '@components/Cta/Cta.jsx';
import { Title } from "@mantine/core";
import Image from "next/image";

const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return null;
    }

    const units = ["B", "KB", "MB", "GB"];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unitIndex]}`;
};

const formatFileType = (contentType, fileName) => {
    if (contentType === "application/pdf") {
        return "PDF";
    }

    if (contentType === "application/msword") {
        return "DOC";
    }

    if (contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
        return "DOCX";
    }

    if (contentType === "application/vnd.ms-excel") {
        return "XLS";
    }

    if (contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        return "XLSX";
    }

    if (contentType === "application/vnd.ms-powerpoint") {
        return "PPT";
    }

    if (contentType === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
        return "PPTX";
    }

    if (contentType === "text/plain") {
        return "TXT";
    }

    if (contentType?.includes("/")) {
        return contentType.split("/")[1].toUpperCase();
    }

    if (fileName?.includes(".")) {
        return fileName.split(".").pop().toUpperCase();
    }

    return "FILE";
};

const renderDocumentLink = (asset, children = null, isInline = false) => {
    const file = asset?.file;
    if (!file?.url) {
        return children;
    }

    const assetUrl = file.url.startsWith("//") ? `https:${file.url}` : file.url;
    const fileType = formatFileType(file.contentType, file.fileName);
    const fileSize = formatFileSize(file.details?.size);
    const linkLabel = children || asset?.title || file.fileName || "Download file";

    if (isInline) {
        return (
            <a href={assetUrl} target="_blank" rel="noopener noreferrer">
                {linkLabel}
            </a>
        );
    }

    return (
        <div className="content-asset">
            <a className="content-asset-link" href={assetUrl} target="_blank" rel="noopener noreferrer">
                {linkLabel}
            </a>
            <p className="content-asset-meta">
                {fileType}
                {fileSize ? ` • ${fileSize}` : ""}
            </p>
        </div>
    );
};

export const renderOptions = {
    renderNode: {
        [INLINES.EMBEDDED_ENTRY]: (node, children) => {
            if (node.data.target.sys.contentType.sys.id === "cta") {
                return (
                    <Cta url={node.data.target.fields.url} title={node.data.target.fields.buttonText} variant={node.data.target.fields.variant} size={node.data.target.fields.size} icon={node.data.target.fields.icon} alignment={node.data.target.fields.iconAlignment} target={node.data.target.fields.newTab} />
                );
            }
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
            if (node.data.target.sys.contentType.sys.id === "video") {
                let host;
                if(node.data.target.fields.host === "Youtube") {
                    host = "https://www.youtube.com/embed/";
                } else if(node.data.target.fields.host === "Vimeo") {
                    host = "https://player.vimeo.com/video/";
                }
                return (
                    <div className="video">
                        <div className="video-player">
                            <iframe
                                src={`${host}${node.data.target.fields.videoId}`}
                                height="100%"
                                width="100%"
                                frameBorder="0"
                                scrolling="no"
                                title={node.data.target.fields.title}
                                allowFullScreen={true}/>
                        </div>
                        {node.data.target.fields.description && <p className="caption">{node.data.target.fields.description}</p>}
                    </div>
                );
            }
            if (node.data.target.sys.contentType.sys.id === "callOutBox") {
                return (
                    <div className="calloutbox">
                        <Title order={3}>{node.data.target.fields.title}</Title>
                        <div className="content-area">
                            {documentToReactComponents(node.data.target.fields.content, renderOptions)} 
                        </div>
                    </div>
                );
            }
            if (node.data.target.sys.contentType.sys.id === "testimonial") {
                return (
                    <blockquote className="testimonial">
                        <p className="quote-text">{node.data.target.fields.quoteText}</p>
                        <p className="quote-author">&mdash; {node.data.target.fields.authorAndRole}</p>
                    </blockquote>
                );
            }
        },
    
        [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
            const asset = node?.data?.target?.fields;
            const file = asset?.file;

            if (!file?.url) {
                return null;
            }

            const isImage = file.contentType?.startsWith("image/") && file.details?.image;
            if (!isImage) {
                return renderDocumentLink(asset);
            }

            const imageUrl = file.url.startsWith("//") ? `https:${file.url}` : file.url;

            return (
                <Image
                    src={`${imageUrl}?w=720`}
                    height={file.details.image.height}
                    width={file.details.image.width}
                    loading="lazy"
                    alt={asset?.title ? asset.title : "Image"}
                />
            );
        },
        [INLINES.ASSET_HYPERLINK]: (node, children) => {
            const asset = node?.data?.target?.fields;
            return renderDocumentLink(asset, children, true);
        },
    },
};
